import { useCallback, useRef, useState } from "react";

export interface PendingActions {
  readonly isPending: (key: string) => boolean;
  readonly run: <T>(
    key: string,
    action: () => Promise<T>,
  ) => Promise<T | undefined>;
}

/** Synchronous operation-scoped lock used by asynchronous UI actions. */
export class PendingActionRegistry {
  readonly #active = new Set<string>();

  public isPending(key: string): boolean {
    return this.#active.has(key);
  }

  public start(key: string): boolean {
    if (this.#active.has(key)) return false;
    this.#active.add(key);
    return true;
  }

  public finish(key: string): void {
    this.#active.delete(key);
  }
}

/** Lock asynchronous UI actions synchronously, scoped by a stable operation key. */
export function usePendingActions(): PendingActions {
  const active = useRef(new PendingActionRegistry());
  const [, render] = useState(0);
  const isPending = useCallback(
    (key: string) => active.current.isPending(key),
    [],
  );
  const run = useCallback(
    async <T>(
      key: string,
      action: () => Promise<T>,
    ): Promise<T | undefined> => {
      if (!active.current.start(key)) return undefined;
      render((value) => value + 1);
      try {
        return await action();
      } finally {
        active.current.finish(key);
        render((value) => value + 1);
      }
    },
    [],
  );
  return { isPending, run };
}

export interface RetryOperationIds {
  readonly complete: (key: string) => void;
  readonly get: (key: string) => string;
}

/** Preserve retry operation IDs until the caller confirms completion. */
export class RetryOperationRegistry {
  readonly #operations = new Map<string, string>();

  public get(key: string, create: () => string): string {
    const current = this.#operations.get(key);
    if (current !== undefined) return current;
    const created = create();
    this.#operations.set(key, created);
    return created;
  }

  public complete(key: string): void {
    this.#operations.delete(key);
  }
}

/** Keep one idempotency key across retries until an operation succeeds. */
export function useRetryOperationIds(): RetryOperationIds {
  const operations = useRef(new RetryOperationRegistry());
  const get = useCallback(
    (key: string): string =>
      operations.current.get(key, () => crypto.randomUUID()),
    [],
  );
  const complete = useCallback((key: string): void => {
    operations.current.complete(key);
  }, []);
  return { complete, get };
}
